<template>
  <div>
    <app-bar>
      <template v-slot:extension>
        <v-tabs v-model="activeTab" class="mx-4" centered>
          <v-tab
            v-for="(item, index) in menuLinks"
            :key="index"
            @click="handleTabClick(item)"
          >{{ item.meta.displayName }}</v-tab>
        </v-tabs>

        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          @click="generatePDF"
          :loading="pdfGenerating"
          class="mr-4"
        >
          <v-icon left>mdi-file-pdf-box</v-icon>
          {{$t('insights.generatePdf.generateButton')}}
        </v-btn>
      </template>
    </app-bar>
    <v-container
      fluid
      class="ma-2"
    >
      <v-row
        class="flex-nowrap"
        align-self="start"
      >
        <v-col cols="auto" class="pl-0">
          <InsightsFilters
            :areas="areas"
            :contracts="contracts"
            :packages="packages"
            :contractors="contractors"
            :projects="projects"
            :currencies="currencies"
            @filtersChanged="handleChangeFilters"
            @dateRangeChanged="handleDateRangeChange"
          />
        </v-col>
        <v-col ref="pdfContent">
          <router-view :filters="filters" :dateRange="dateRange" :pdfMode="pdfMode"/>
        </v-col>
      </v-row>
      <v-spacer></v-spacer>
    </v-container>
  </div>
</template>

<script>
import { getContracts, getAllDictionariesForInsights } from '@/services'
import { generateInsightsPDF, InsightsEventBus } from '@/utils/insightsPdfUtils'

export default {
  name: 'InsightsView',
  data () {
    return {
      filters: [],
      dateRange: [],
      dictionaries: [],
      activeTab: 0,
      dictionaryFetching: 0,
      pdfGenerating: false,
      pdfMode: false,
      waitingForCharts: false
    }
  },
  methods: {
    handleChangeFilters (filters) {
      this.filters = filters
    },
    handleDateRangeChange (dateRange) {
      this.dateRange = dateRange
    },
    handleTabClick (item) {
      this.$route.name !== item.name &&
      this.$router.push({
        name: item.name
      })
    },
    loadInitialTab () {
      this.cancelPdfGeneration()
      const tab = this.menuLinks.find((i) => i.name === this.$route.name)
      this.activeTab = tab ? this.menuLinks.indexOf(tab) : 0

      if (this.$route.name === 'insights') {
        this.$router.push({
          name: this.menuLinks[0].name
        })
      }
    },

    async generatePDF () {
      this.pdfGenerating = true
      this.pdfMode = true
      this.waitingForCharts = true

      try {
        await this.$nextTick()
      } catch (error) {
        console.error('❌ Error during PDF setup:', error)
        this.$eventBus.$emit('SHOW_MESSAGE', {
          text: this.$t('insights.generatePdf.pdfFailedMessage', { message: error }),
          color: 'error',
          timeout: 6000,
          isRead: false
        })

        this.pdfGenerating = false
        this.pdfMode = false
        this.waitingForCharts = false
      }
    },

    async handleAllChartsReady () {
      this.waitingForCharts = false

      try {
        await generateInsightsPDF(this.$refs.pdfContent)

        this.$eventBus.$emit('SHOW_MESSAGE', {
          text: this.$t('insights.generatePdf.pdfSuccessMessage'),
          color: 'success',
          timeout: 4000,
          isRead: false
        })
      } catch (error) {
        this.$eventBus.$emit('SHOW_MESSAGE', {
          text: this.$t('insights.generatePdf.pdfFailedMessage', { message: error }),
          color: 'error',
          timeout: 6000,
          isRead: false
        })
      } finally {
        this.pdfGenerating = false
        this.pdfMode = false
      }
    },
    cancelPdfGeneration () {
      if (this.pdfGenerating || this.pdfMode || this.waitingForCharts) {
        this.pdfGenerating = false
        this.pdfMode = false
        this.waitingForCharts = false
      }
    }
  },
  async beforeMount () {
    try {
      this.dictionaryFetching = 1
      const [dictionaries] = await Promise.all([
        getAllDictionariesForInsights(),
        getContracts()
      ])
      this.dictionaries = dictionaries
    } catch (error) {
      console.error('Failed to load dictionaries:', error)
    } finally {
      this.dictionaryFetching = 0
    }
  },
  computed: {
    areas () {
      return this.dictionaries.areas || []
    },
    contracts () {
      return this.$store.state.contracts || []
    },
    packages () {
      const packages = [...(this.dictionaries.packages || [])]
      const names = new Set(packages.map(item => item.name))

      this.contracts.forEach(contract => {
        const name = contract.package
        if (typeof name === 'string' && name.trim() && !names.has(name)) {
          packages.push({ id: `historical:${name}`, name })
          names.add(name)
        }
      })

      return packages
    },
    contractors () {
      return this.dictionaries.contractors || []
    },
    projects () {
      return this.dictionaries.projects || []
    },
    currencies () {
      return this.dictionaries.currencies || []
    },
    menuLinks () {
      return this.$router.options.routes
        .find((r) => r.name === 'insights')
        .children.filter((i) => i.show) || []
    }
  },
  mounted () {
    this.loadInitialTab()

    InsightsEventBus.$on('all-charts-ready-for-pdf', this.handleAllChartsReady)
  },

  beforeDestroy () {
    InsightsEventBus.$off('all-charts-ready-for-pdf', this.handleAllChartsReady)
  },
  watch: {
    '$route.name': function (newName) {
      this.cancelPdfGeneration()
      if (newName === 'insights') {
        this.loadInitialTab()
      }
    }
  }
}
</script>
